export class User {
    constructor(
        public readonly id: string,
        public readonly email: string,
        public readonly authId: string,
        public readonly phoneNumber: string,
        public readonly name: string,
        public readonly dateOfBirth: Date,
        public readonly occupation: string,
        public readonly company: string,
        public readonly salary: string,
        public readonly maritalStatus: string,
        public readonly gender: string,
        public readonly digitalAddress: string,
        public readonly creditAmount: number,
        public readonly spendingLimit: number,
        public readonly status: UserStatus,
        public readonly progress: UserProgress,
        public readonly signUpState: SignUpState,
        public readonly creditState: CreditState,
        public readonly identityVerification: boolean,
        public readonly skipDueDiligence: boolean,
        public readonly completedDueDiligence: boolean,
        public readonly isWhatsapp: boolean,
        public readonly whatsappNumber?: string,
        public readonly photoUrl?: string,
        public readonly altOccupation?: string,
        public readonly incomeStatus?: string,
        public readonly incomeMode?: string,
        public readonly authorizationIssuedDate?: number,
        public readonly clientUpdatedTime?: number,
        public readonly startTimestamp?: Date,
        public readonly authorizationRequestTimestamp?: Date
    ) {
    }

    static create(params: UserCreateParams): User {
        return new User(
            params.id,
            params.email,
            params.authId,
            params.phoneNumber,
            params.name,
            params.dateOfBirth,
            params.occupation,
            params.company,
            params.salary,
            params.maritalStatus,
            params.gender,
            params.digitalAddress,
            params.creditAmount || 0,
            params.spendingLimit || 0,
            params.status || UserStatus.PENDING,
            params.progress || UserProgress.STARTED,
            params.signUpState || SignUpState.INITIATED,
            params.creditState || CreditState.INACTIVE,
            params.identityVerification || false,
            params.skipDueDiligence || false,
            params.completedDueDiligence || false,
            params.isWhatsapp || false,
            params.whatsappNumber,
            params.photoUrl,
            params.altOccupation,
            params.incomeStatus,
            params.incomeMode,
            params.authorizationIssuedDate,
            params.clientUpdatedTime,
            params.startTimestamp,
            params.authorizationRequestTimestamp
        );
    }

    updateProfile(updates: Partial<UserUpdateParams>): User {
        return new User(
            this.id,
            updates.email ?? this.email,
            this.authId,
            updates.phoneNumber ?? this.phoneNumber,
            updates.name ?? this.name,
            updates.dateOfBirth ?? this.dateOfBirth,
            updates.occupation ?? this.occupation,
            updates.company ?? this.company,
            updates.salary ?? this.salary,
            updates.maritalStatus ?? this.maritalStatus,
            updates.gender ?? this.gender,
            updates.digitalAddress ?? this.digitalAddress,
            updates.creditAmount ?? this.creditAmount,
            updates.spendingLimit ?? this.spendingLimit,
            updates.status ?? this.status,
            updates.progress ?? this.progress,
            updates.signUpState ?? this.signUpState,
            updates.creditState ?? this.creditState,
            updates.identityVerification ?? this.identityVerification,
            updates.skipDueDiligence ?? this.skipDueDiligence,
            updates.completedDueDiligence ?? this.completedDueDiligence,
            updates.isWhatsapp ?? this.isWhatsapp,
            updates.whatsappNumber ?? this.whatsappNumber,
            updates.photoUrl ?? this.photoUrl,
            updates.altOccupation ?? this.altOccupation,
            updates.incomeStatus ?? this.incomeStatus,
            updates.incomeMode ?? this.incomeMode,
            updates.authorizationIssuedDate ?? this.authorizationIssuedDate,
            Date.now(),
            updates.startTimestamp ?? this.startTimestamp,
            updates.authorizationRequestTimestamp ?? this.authorizationRequestTimestamp
        );
    }

    isEligibleForCredit(): boolean {
        return (
            this.identityVerification &&
            this.completedDueDiligence &&
            this.status === UserStatus.ACTIVE &&
            this.creditAmount > 0
        );
    }

    canMakePurchase(amount: number): boolean {
        return this.isEligibleForCredit() && amount <= this.spendingLimit;
    }
}

export enum UserStatus {
    PENDING = 'pending',
    ACTIVE = 'active',
    SUSPENDED = 'suspended',
    INACTIVE = 'inactive'
}

export enum UserProgress {
    STARTED = 'started',
    KYC_PENDING = 'kyc_pending',
    KYC_COMPLETED = 'kyc_completed',
    DOCUMENTS_PENDING = 'documents_pending',
    COMPLETED = 'completed'
}

export enum SignUpState {
    INITIATED = 'initiated',
    OTP_VERIFICATION = 'otp_verification',
    PROFILE_SETUP = 'profile_setup',
    DOCUMENT_UPLOAD = 'document_upload',
    COMPLETED = 'completed'
}

export enum CreditState {
    INACTIVE = 'inactive',
    PENDING = 'pending',
    APPROVED = 'approved',
    SUSPENDED = 'suspended'
}

export interface UserCreateParams {
    id: string;
    email: string;
    authId: string;
    phoneNumber: string;
    name: string;
    dateOfBirth: Date;
    occupation: string;
    company: string;
    salary: string;
    maritalStatus: string;
    gender: string;
    digitalAddress: string;
    creditAmount?: number;
    spendingLimit?: number;
    status?: UserStatus;
    progress?: UserProgress;
    signUpState?: SignUpState;
    creditState?: CreditState;
    identityVerification?: boolean;
    skipDueDiligence?: boolean;
    completedDueDiligence?: boolean;
    isWhatsapp?: boolean;
    whatsappNumber?: string;
    photoUrl?: string;
    altOccupation?: string;
    incomeStatus?: string;
    incomeMode?: string;
    authorizationIssuedDate?: number;
    clientUpdatedTime?: number;
    startTimestamp?: Date;
    authorizationRequestTimestamp?: Date;
}

export interface UserUpdateParams extends Partial<Omit<UserCreateParams, 'id' | 'authId'>> {
}